import { c as _c } from "react/compiler-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../../dropdown";
import { FilterPill } from "../../filter-pill";
import Tooltip from "../../tooltip";
import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoIosClose } from "react-icons/io";
import Button from "@/components/ui/button";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { cx } from "@/lib/tailwind-utils";
import { FilterEditContext, type FilterEditContextValue } from "./edit-context";
import { formatFilterDisplayValue } from "./helpers";
import { useFilterFocus } from "./hooks";
import { FilterInput, filterContentWidthClass } from "./render-input";
import type { FilterTypeProps } from "./types";
const isEmptyValue = (value: any) => value === undefined || value === null || value === "" || Array.isArray(value) && value.length === 0;

/**
 * InlinePillTableFilter — renders a filter as a chip outside the main
 * Filters dropdown.
 *
 * Write semantics: edits buffer in local state and publish to `filterSetter`
 * on close (blur). This keeps the table from refetching on every keystroke
 * and keeps the pill mounted while editing.
 */
const InlinePillTableFilter: React.FC<FilterTypeProps> = t0 => {
  const $ = _c(66);
  const {
    label,
    type,
    filterValue,
    filterSetter,
    options,
    className,
    disableClear: t1,
    icon: Icon,
    onBack,
    direction
  } = t0;
  const disableClear = t1 === undefined ? false : t1;
  const {
    focusRef,
    inputRef
  } = useFilterFocus();
  const clock = useClinicClock();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(filterValue);
  const pendingValueRef = useRef(filterValue);
  const justClearedRef = useRef(false);
  let t2;
  let t3;
  if ($[0] !== filterValue || $[1] !== isOpen) {
    t2 = () => {
      if (!isOpen) {
        pendingValueRef.current = filterValue;
        setPendingValue(filterValue);
      }
    };
    t3 = [filterValue, isOpen];
    $[0] = filterValue;
    $[1] = isOpen;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => {
      pendingValueRef.current = value;
      setPendingValue(value);
      justClearedRef.current = false;
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleChange = t4;
  let t5;
  if ($[5] !== filterSetter || $[6] !== filterValue) {
    t5 = open => {
      setIsOpen(open);
      if (open) {
        pendingValueRef.current = filterValue;
        setPendingValue(filterValue);
        justClearedRef.current = false;
        return;
      }
      if (justClearedRef.current) {
        justClearedRef.current = false;
        return;
      }
      const next = isEmptyValue(pendingValueRef.current) ? undefined : pendingValueRef.current;
      const unchanged = next === undefined && isEmptyValue(filterValue) || isEqual(next, filterValue);
      if (!unchanged) {
        filterSetter(next);
      }
    };
    $[5] = filterSetter;
    $[6] = filterValue;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[8] !== filterSetter) {
    t6 = e => {
      e.stopPropagation();
      pendingValueRef.current = undefined;
      setPendingValue(undefined);
      justClearedRef.current = true;
      filterSetter(undefined);
    };
    $[8] = filterSetter;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleClearFilter = t6;
  let t7;
  if ($[10] !== pendingValue) {
    t7 = {
      value: pendingValue
    };
    $[10] = pendingValue;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== handleOpenChange) {
    t8 = {
      onChange: handleChange,
      submit: () => handleOpenChange(false)
    };
    $[12] = handleOpenChange;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== direction || $[15] !== focusRef || $[16] !== label || $[17] !== onBack || $[18] !== options || $[19] !== type) {
    t9 = {
      type,
      label,
      options,
      onBack,
      focusRef,
      direction
    };
    $[14] = direction;
    $[15] = focusRef;
    $[16] = label;
    $[17] = onBack;
    $[18] = options;
    $[19] = type;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t10 = {
      state: t7,
      actions: t8,
      meta: t9
    };
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const editContextValue = t10;
  let t11;
  if ($[25] !== inputRef) {
    t11 = e_0 => {
      if (e_0.target !== e_0.currentTarget) {
        return;
      }
      const el = e_0.currentTarget;
      if (el instanceof HTMLElement && el.dataset.state === "open" && inputRef.current) {
        inputRef.current.focus();
      }
    };
    $[25] = inputRef;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const handleContentAnimationEnd = t11;
  if (options?.length === 0) {
    return null;
  }
  const displaySource = isOpen ? pendingValue : filterValue;
  let t12;
  if ($[27] !== clock || $[28] !== displaySource || $[29] !== options || $[30] !== type) {
    t12 = formatFilterDisplayValue(displaySource, type, options, clock);
    $[27] = clock;
    $[28] = displaySource;
    $[29] = options;
    $[30] = type;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const {
    fullDisplayValue,
    truncatedDisplayValue
  } = t12;
  let t13;
  if ($[32] !== disableClear || $[33] !== displaySource || $[34] !== handleClearFilter) {
    t13 = !isEmptyValue(displaySource) && !disableClear ? <Button variant="ghost" icon={IoIosClose} onMouseDown={handleClearFilter} className="relative z-20 mr-1 !p-0" /> : null;
    $[32] = disableClear;
    $[33] = displaySource;
    $[34] = handleClearFilter;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const clearButton = t13;
  let t14;
  if ($[36] !== Icon) {
    t14 = Icon ? <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" /> : null;
    $[36] = Icon;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== displaySource || $[39] !== fullDisplayValue || $[40] !== truncatedDisplayValue) {
    t15 = !isEmptyValue(displaySource) && truncatedDisplayValue && <><div className="mx-2 h-3 self-center border-l" /><Tooltip content={fullDisplayValue} triggerAsChild={true}><span className="text-caption-emphasis text-primary">{truncatedDisplayValue}</span></Tooltip></>;
    $[38] = displaySource;
    $[39] = fullDisplayValue;
    $[40] = truncatedDisplayValue;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== label || $[43] !== t14 || $[44] !== t15) {
    t16 = <>{t14}{label}{t15}</>;
    $[42] = label;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const triggerContent = t16;
  let t17;
  if ($[46] !== triggerContent) {
    t17 = <DropdownMenuTrigger className="flex items-center">{triggerContent}</DropdownMenuTrigger>;
    $[46] = triggerContent;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== className || $[49] !== type) {
    t18 = cx("min-w-0 overflow-visible", filterContentWidthClass(type), className);
    $[48] = className;
    $[49] = type;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="min-w-[min(200px,calc(var(--radix-popper-available-width)-12px))] px-1.5 py-1"><FilterInput /></div>;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== editContextValue) {
    t20 = <FilterEditContext value={editContextValue}>{t19}</FilterEditContext>;
    $[52] = editContextValue;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== handleContentAnimationEnd || $[55] !== t18 || $[56] !== t20) {
    t21 = <DropdownMenuContent className={t18} align="start" onAnimationEnd={handleContentAnimationEnd}>{t20}</DropdownMenuContent>;
    $[54] = handleContentAnimationEnd;
    $[55] = t18;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== handleOpenChange || $[59] !== isOpen || $[60] !== t17 || $[61] !== t21) {
    t22 = <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>{t17}{t21}</DropdownMenu>;
    $[58] = handleOpenChange;
    $[59] = isOpen;
    $[60] = t17;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== clearButton || $[64] !== t22) {
    t23 = <FilterPill as="div" className="w-auto overflow-hidden whitespace-nowrap px-2 py-1 shadow-sm transition-all duration-300">{clearButton}{t22}</FilterPill>;
    $[63] = clearButton;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  return t23;
};
export default InlinePillTableFilter;